import { useEffect, useState, useCallback, useRef } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { usePermissions } from "@/lib/permissions";
import { apiErrorMessage } from "@/lib/api-error";
import { invoiceStateValues, type components } from "@dolfin/api-public";
import { enumOptions } from "@/lib/enum-options";
import { EnumMultiSelect } from "@/components/EnumMultiSelect";
import { Button } from "@/components/ui/button";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { Ban, FileText, MoreHorizontal, Plus, Send } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/format";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import {
  DateRangeFilter,
  EmptyState,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  rowLink,
  type DateRange,
} from "@/components/PageUi";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type InvoiceResponse = components["schemas"]["InvoiceResponse"];
type InvoiceState = components["schemas"]["InvoiceState"];

// No `all` option: the multi-select treats an empty selection as "no filter".
const STATUSES = enumOptions(invoiceStateValues);

const PAGE_SIZE = 10;

// Words for the statuses whose enum name is not already plain English.
const STATUS_LABELS: Record<string, string> = {
  PartiallyPaid: "Partly paid",
};

export function InvoiceStatusPill({ status }: { status: string | undefined }) {
  const tone =
    status === "Paid" || status === "Credited"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
      : status === "Issued"
        ? "border-accent-blue/30 bg-accent-blue/10 text-accent-blue"
        : status === "Void"
          ? "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400"
          : status === "PartiallyPaid"
            ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400"
            : "border-border bg-muted text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex min-w-[64px] shrink-0 justify-center rounded-md border px-2 py-0.5 text-[11px] font-medium",
        tone,
      )}
    >
      {status ? (STATUS_LABELS[status] ?? status) : "-"}
    </span>
  );
}

function isOverdue(invoice: InvoiceResponse, today: Date) {
  return (
    !!invoice.dueDate &&
    (invoice.status === "Issued" || invoice.status === "PartiallyPaid") &&
    new Date(invoice.dueDate) < today
  );
}

export interface InvoicesListFilters {
  /** Zero or more invoice states to filter by. Empty/undefined = all states. */
  states?: InvoiceState[];
  dateFrom?: string;
  dateTo?: string;
}

export interface InvoicesListPageProps {
  onInvoiceClick: (id: string) => void;
  onCreateInvoice: () => void;
  onSendInvoice: (id: string) => void;
  onCustomerClick: (id: string) => void;
  /** Initial filters. Updates to filters live in component state. */
  defaultFilters?: InvoicesListFilters;
  onFiltersChange?: (filters: InvoicesListFilters) => void;
}

export function InvoicesListPage({
  onInvoiceClick,
  onCreateInvoice,
  onSendInvoice,
  onCustomerClick,
  defaultFilters,
  onFiltersChange,
}: InvoicesListPageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canManage = can("manageInvoices");

  const [invoices, setInvoices] = useState<InvoiceResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [voidingId, setVoidingId] = useState<string | null>(null);
  const [invoiceToVoid, setInvoiceToVoid] = useState<InvoiceResponse | null>(null);

  const [stateFilter, setStateFilter] = useState<InvoiceState[]>(defaultFilters?.states ?? []);
  const [dateFrom, setDateFrom] = useState<string>(defaultFilters?.dateFrom ?? "");
  const [dateTo, setDateTo] = useState<string>(defaultFilters?.dateTo ?? "");

  // Ref so a fresh inline callback doesn't re-fire the effect (an emit loop via e.g. setSearchParams).
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  const firstEmit = useRef(true);

  useEffect(() => {
    if (firstEmit.current) {
      // Host already passed these values as defaultFilters; nothing to sync.
      firstEmit.current = false;
      return;
    }
    onFiltersChangeRef.current?.({
      states: stateFilter.length > 0 ? stateFilter : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
  }, [stateFilter, dateFrom, dateTo]);

  const fetchInvoices = useCallback(async (cursor?: string) => {
    setLoading(true);
    setError(null);

    try {
      const query: Record<string, string | number | InvoiceState[]> = { Limit: PAGE_SIZE };
      if (stateFilter.length > 0) query.States = stateFilter;
      if (dateFrom) query.IssueDateFrom = dateFrom;
      if (dateTo) query.IssueDateTo = dateTo;
      if (cursor) query.Cursor = cursor;

      const { data, error: apiError } = await api.GET("/v1/invoices", {
        params: { query },
      });

      if (apiError || !data) {
        console.error("Failed to load invoices:", apiError);
        setError("Failed to load invoices");
        return;
      }

      setInvoices(data.data ?? []);
      const next = data.meta?.cursors?.next ?? null;
      setNextCursor(next);
      setHasMore(next !== null);
    } catch (e) {
      console.error("Failed to load invoices:", e);
      setError("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [api, stateFilter, dateFrom, dateTo]);

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchInvoices();
  }, [fetchInvoices]);

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchInvoices(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchInvoices(prevCursor);
  }

  async function confirmVoid() {
    const target = invoiceToVoid;
    if (!target?.id) return;
    const id = target.id;
    setInvoiceToVoid(null);
    setVoidingId(id);
    const { error: apiError } = await api.DELETE("/v1/invoices/{id}", {
      params: { path: { id } },
    });
    setVoidingId(null);

    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to void invoice"));
      return;
    }

    toast.success("Invoice voided");
    setCursorStack([]);
    setNextCursor(null);
    fetchInvoices();
  }

  const currentPage = cursorStack.length + 1;
  const filtered = stateFilter.length > 0 || !!dateFrom || !!dateTo;
  const today = new Date(new Date().toDateString());

  function setDates(range: DateRange) {
    setDateFrom(range.from ?? "");
    setDateTo(range.to ?? "");
  }

  function clearFilters() {
    setStateFilter([]);
    setDates({});
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Receivables"
        title="Invoices"
        subtitle="Drafts, sent invoices and what is still owed. Open one to send it, chase it or record a payment."
        actions={
          <PermissionGuard allowed={canManage} reason="Only owners and admins can create invoices" className="w-full sm:w-auto">
            <Button onClick={onCreateInvoice} className="w-full gap-1.5 sm:w-auto">
              <Plus className="size-4" />
              New invoice
            </Button>
          </PermissionGuard>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <EnumMultiSelect
          options={STATUSES}
          value={stateFilter}
          onChange={setStateFilter}
          placeholder="All statuses"
          searchPlaceholder="Search statuses..."
          className="w-full sm:w-[200px]"
        />
        {/* Issue date, so "last month's invoices" is one pick. */}
        <DateRangeFilter value={{ from: dateFrom || undefined, to: dateTo || undefined }} onChange={setDates} />
      </div>

      {loading ? (
        <LoadingState label="Loading invoices..." />
      ) : error ? (
        <ErrorState message="Invoices could not be loaded." onRetry={() => fetchInvoices(cursorStack[cursorStack.length - 1])} />
      ) : invoices.length === 0 ? (
        filtered ? (
          <EmptyState
            icon={FileText}
            title="No invoices match these filters"
            action={
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          >
            Try a wider date range or another status.
          </EmptyState>
        ) : (
          <EmptyState
            icon={FileText}
            title="No invoices yet"
            action={
              <PermissionGuard allowed={canManage} reason="Only owners and admins can create invoices">
                <Button size="sm" onClick={onCreateInvoice}>
                  <Plus className="size-4" /> New invoice
                </Button>
              </PermissionGuard>
            }
          >
            Create an invoice, send it, and payments against it show up here.
          </EmptyState>
        )
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Customer</TableHead>
                  <TableHead className={TH}>Due</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Outstanding</TableHead>
                  <TableHead className={cn(TH, "w-[100px] text-right")}>Status</TableHead>
                  <TableHead className="w-[48px]" />
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => {
                  const isVoiding = voidingId === invoice.id;
                  const isDraft = invoice.status === "Draft";
                  const isVoidable =
                    invoice.status === "Issued" || invoice.status === "PartiallyPaid";
                  const showSend = isDraft;
                  const showVoid = canManage && isVoidable;
                  const showMenu = showSend || showVoid;
                  const overdue = isOverdue(invoice, today);
                  // Overpaid invoices go negative; what is owed back is shown on the invoice, not here.
                  const outstanding = Math.max(Number(invoice.outstanding ?? 0), 0);
                  // A draft is not owed yet and a void never will be.
                  const owes = !isDraft && invoice.status !== "Void";
                  const customerName = invoice.customer?.name ?? "Unknown customer";
                  return (
                    <TableRow
                      key={invoice.id}
                      {...rowLink(invoice.id ? () => onInvoiceClick(invoice.id!) : undefined)}
                    >
                      <TableCell>
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="min-w-0">
                            <button
                              type="button"
                              className="block max-w-full truncate text-left font-semibold hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                onCustomerClick(invoice.customerId!);
                              }}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              {customerName}
                            </button>
                            <div className="truncate text-xs text-muted-foreground">
                              <span className="font-mono">{invoice.documentNumber || "Draft"}</span>
                              {invoice.issueDate && !isDraft ? ` · Issued ${formatDate(invoice.issueDate)}` : null}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {invoice.status === "Paid" || invoice.status === "Credited" ? (
                          <span className="text-muted-foreground">Settled</span>
                        ) : invoice.status === "Void" ? (
                          <span className="text-muted-foreground">-</span>
                        ) : overdue ? (
                          <span className="font-medium text-red-600 dark:text-red-400">
                            Overdue · {formatDate(invoice.dueDate)}
                          </span>
                        ) : invoice.dueDate ? (
                          <span className="text-muted-foreground">Due {formatDate(invoice.dueDate)}</span>
                        ) : isDraft ? (
                          <span className="text-muted-foreground">
                            Created {formatDate(invoice.createdAt ?? invoice.issueDate)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">{formatDate(invoice.issueDate)}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="tabular-nums">
                          {formatCurrency(invoice.totalAmount, invoice.currency)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {!owes ? (
                          <span className="text-muted-foreground">-</span>
                        ) : outstanding > 0 ? (
                          <span className={cn("font-semibold tabular-nums", overdue && "text-red-600 dark:text-red-400")}>
                            {formatCurrency(outstanding, invoice.currency)}
                          </span>
                        ) : (
                          <span className="tabular-nums text-muted-foreground">
                            {formatCurrency(0, invoice.currency)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <InvoiceStatusPill status={invoice.status ?? undefined} />
                      </TableCell>
                      <TableCell className="text-right">
                        {showMenu && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={isVoiding}
                                className="size-8"
                                aria-label="Invoice actions"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                {isVoiding ? (
                                  <Spinner className="size-4" />
                                ) : (
                                  <MoreHorizontal className="size-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-44"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {showSend && invoice.id && (
                                <DropdownMenuItem
                                  onClick={() => onSendInvoice(invoice.id!)}
                                >
                                  <Send className="mr-2 size-4" />
                                  Send invoice
                                </DropdownMenuItem>
                              )}
                              {showVoid && (
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setInvoiceToVoid(invoice)}
                                >
                                  <Ban className="mr-2 size-4" />
                                  Void invoice
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>

          <Pager
            page={currentPage}
            hasPrevious={currentPage > 1}
            hasNext={hasMore}
            onPrevious={handlePrevPage}
            onNext={handleNextPage}
          />
        </>
      )}

      <AlertDialog
        open={invoiceToVoid !== null}
        onOpenChange={(open) => !open && setInvoiceToVoid(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              Invoice {invoiceToVoid?.documentNumber || invoiceToVoid?.id} will be marked as Void. The record is retained for your audit history but it can no longer be sent or paid.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmVoid}>
              Void invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
